import { useQuery } from '@apollo/client';
import React from 'react';

import styled from 'styled-components';
import AdminModal from '../AdminModal';
import OrganizationImpersonate from './OrganizationImpersonate';
import { ModalMenu } from '@ibinder/ui-common-dialogs';
import { GET_ORGANIZATION } from 'Shared/api/queries/getOrganization.query';
import {
  GET_ALL_ORGANIZATION_FEATURES,
  GET_ALL_PACKAGES,
  GET_ORG_PACKAGES
} from 'Shared/api/queries/getOrganizationPackages.query';
import OrganizationInfo from './OrganizationInfo';
import OrganizationPackages from './OrganizationPackages';
import OrganizationFeatures from './OrganizationFeatures';
import { useIBTranslation } from '@ibinder/ui-common-hooks';
import CreateOrgForm from '../CreateOrgForm';

const ModalWrapper = styled.div`
  padding: 1rem;
  height: 100%;
  overflow-x: auto;
`;
export enum OrgModalNavTabs {
  CREATE = 'Create',
  INFO = 'Info',
  IMPERSONATE = 'Impersonate',
  PACKAGES = 'Packages',
  FEATURES = 'Features'
}

const OrgViewModal = ({
  onClose,
  orgId = [],
  type,
  PackagesAndFeaturesForOrganizationInAdmin,
  Configcat_selectPackageOnOrgCreation,
  isCreateOrg = false,
  setIsCreateOrg,
  handleSetOrganization,
  redirectFromCreateOrg
}) => {
  const showDoneModalNode = React.useRef();

  const { t } = useIBTranslation();

  const confirmDiscardIfDirty = (currentTabLabel?: string) => {
    if (isInfoDirtyRef.current) {
      const tabName = currentTabLabel ?? item;
      const proceed = window.confirm(
        t('OrganizationMenu.unsavedChangesConfirm', { tabName })
      );
      if (!proceed) {
        return false;
      }
      // User chose to discard; clear dirty flag so subsequent actions are not blocked
      isInfoDirtyRef.current = false;
    }
    return true;
  };

  const onCloseModal = () => {
    if (!confirmDiscardIfDirty(item)) return;
    localStorage.removeItem('tenantId');
    onClose();
  };
  const organization = useQuery(GET_ORGANIZATION, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      organizationId: orgId
    },
    skip: orgId.length === 0
  });

  const AllPackages = useQuery(GET_ALL_PACKAGES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });
  const organizationPackages = useQuery(GET_ORG_PACKAGES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      organizationId: orgId
    },
    skip: orgId.length === 0
  });

  const AllFeaturesForOrganization = useQuery(GET_ALL_ORGANIZATION_FEATURES, {
    variables: { orgId },
    skip: orgId.length === 0
  });

  const menuItems = isCreateOrg
    ? [OrgModalNavTabs.CREATE]
    : Configcat_selectPackageOnOrgCreation
    ? [
        OrgModalNavTabs.IMPERSONATE,
        OrgModalNavTabs.INFO,
        OrgModalNavTabs.PACKAGES,
        OrgModalNavTabs.FEATURES
      ]
    : [
        OrgModalNavTabs.IMPERSONATE,
        OrgModalNavTabs.INFO,
        OrgModalNavTabs.FEATURES
      ];

  const [item, setItem] = React.useState<string>(() => {
    if (isCreateOrg) {
      return t(`OrganizationMenu.${OrgModalNavTabs.CREATE}`);
    } else if (redirectFromCreateOrg) {
      return t(`OrganizationMenu.${OrgModalNavTabs.INFO}`);
    } else {
      return t(`OrganizationMenu.${OrgModalNavTabs.IMPERSONATE}`);
    }
  });
  const isInfoDirtyRef = React.useRef<boolean>(false);

  const handleInfoDirtyChange = React.useCallback((v: boolean) => {
    // Use ref instead of state to avoid re-rendering
    isInfoDirtyRef.current = v;
  }, []);

  const loading =
    organization.loading || organizationPackages.loading || AllPackages.loading;

  const handleSelectTab = (e: string) => {
    if (loading) return;
    if (!confirmDiscardIfDirty(item)) return;
    setItem(e);
  };

  return (
    <AdminModal
      ref={showDoneModalNode}
      onClose={onCloseModal}
      loading={loading}
      label={
        isCreateOrg
          ? t(`OrganizationMenu.${OrgModalNavTabs.CREATE}`)
          : organization?.data?.get?.organization?.name ?? 'Loading...'
      }
      name={
        isCreateOrg
          ? t(`OrganizationMenu.${OrgModalNavTabs.CREATE}`)
          : organization?.data?.get?.organization?.name ?? 'Loading...'
      }
      Component={() => (
        <>
          {isCreateOrg ? (
            <CreateOrgForm
              onClose={onClose}
              setIsCreateOrg={setIsCreateOrg}
              handleSetOrganization={handleSetOrganization}
            />
          ) : (
            <>
              <ModalMenu
                menuItems={menuItems.map((key) => t(`OrganizationMenu.${key}`))}
                onSelect={handleSelectTab}
                selected={item}
              />
              <ModalWrapper>
                {item ===
                  t(`OrganizationMenu.${OrgModalNavTabs.IMPERSONATE}`) && (
                  <OrganizationImpersonate
                    organization={organization}
                    type={type}
                  />
                )}
                {item === t(`OrganizationMenu.${OrgModalNavTabs.INFO}`) && (
                  <OrganizationInfo
                    organizationData={organization?.data?.get?.organization}
                    platform={organization?.data?.get?.platform}
                    onDirtyChange={handleInfoDirtyChange}
                  />
                )}
                {item === t(`OrganizationMenu.${OrgModalNavTabs.PACKAGES}`) && (
                  <OrganizationPackages
                    organizationData={organization?.data?.get?.organization}
                    refetchOrgFeatures={AllFeaturesForOrganization.refetch}
                    {...{ organizationPackages, AllPackages }}
                  />
                )}
                {item === t(`OrganizationMenu.${OrgModalNavTabs.FEATURES}`) && (
                  <OrganizationFeatures
                    {...{
                      canToggleFeatures:
                        PackagesAndFeaturesForOrganizationInAdmin ||
                        Configcat_selectPackageOnOrgCreation
                    }}
                    organizationData={organization?.data?.get?.organization}
                    isLegacyPackage={
                      organizationPackages.data.get[0]?.legacyPackage
                    }
                    AllFeaturesForOrganization={
                      AllFeaturesForOrganization?.data?.get
                    }
                  />
                )}
              </ModalWrapper>
            </>
          )}
        </>
      )}
    />
  );
};

export default OrgViewModal;
